import { CorpusId, WidgetStateId } from '../../models/aliases';
import {
  FacetName,
  OrderBy,
  PaperFilter,
  RoundStatus,
  WidgetState,
} from '../../models/widget-state';
import { getAtOffset, SpeculatedArray } from '../../utils/speculated-array';
import {
  Message,
  StateUpdateMessage,
  WidgetActionMessage,
} from '../../models/threads';
import { PaperFinderWidgetAction } from '../../models/widget-actions';

import { enc, MD5 } from 'crypto-js';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';
import sortBy from 'lodash-es/sortBy';
import stableStringify from 'json-stable-stringify';

export function reduceActionToMessages(
  conversation: SpeculatedArray<Message>,
  state: WidgetState,
  message: WidgetActionMessage,
): [StateUpdateMessage | undefined, WidgetState] {
  const action = message.action;
  let stateId: WidgetStateId;
  let status: RoundStatus['kind'];
  let prevStateOffset: number | null;
  let nextStateOffset: number | null;
  let updatedState = state;

  const currentStateMessage = getAtOffset(
    conversation,
    message.predecessorOffsetInThread,
  ) as StateUpdateMessage;
  const widgetId = currentStateMessage.widgetId;

  switch (action.kind) {
    case 'undo': {
      let prevStateMessage: StateUpdateMessage | undefined;
      if (currentStateMessage.prevStateOffsetInThread) {
        prevStateMessage = getAtOffset(
          conversation,
          currentStateMessage.prevStateOffsetInThread,
        ) as StateUpdateMessage;
      }

      if (prevStateMessage) {
        stateId = prevStateMessage.stateId;
        status = prevStateMessage.status;
        nextStateOffset = message.predecessorOffsetInThread;
        prevStateOffset = prevStateMessage.prevStateOffsetInThread;
      } else {
        // no update message required, state doesn't change
        return [undefined, state];
      }
      break;
    }
    case 'redo': {
      let nextStateMessage: StateUpdateMessage | undefined;
      if (currentStateMessage.nextStateOffsetInThread) {
        nextStateMessage = getAtOffset(
          conversation,
          currentStateMessage.nextStateOffsetInThread,
        ) as StateUpdateMessage;
      }

      if (nextStateMessage) {
        stateId = nextStateMessage.stateId;
        status = nextStateMessage.status;
        nextStateOffset = nextStateMessage.nextStateOffsetInThread;
        prevStateOffset = nextStateMessage.prevStateOffsetInThread;
      } else {
        // no update message required, state doesn't change
        return [undefined, state];
      }
      break;
    }
    default: {
      updatedState = reduceWidgetState(state, action);
      stateId = generateWidgetStateId(updatedState);
      status = updatedState.roundStatus.kind;
      nextStateOffset = null;
      prevStateOffset = message.predecessorOffsetInThread;
    }
  }

  const updateMessage: StateUpdateMessage = {
    kind: 'widget-state-update',
    widgetId,
    status,
    stateId,
    offsetInThread: message.offsetInThread + 1,
    predecessorOffsetInThread: message.offsetInThread,
    nextStateOffsetInThread: nextStateOffset,
    prevStateOffsetInThread: prevStateOffset,
  };

  return [updateMessage, updatedState];
}

function reduceWidgetState(
  state: WidgetState,
  action: PaperFinderWidgetAction,
): WidgetState {
  switch (action.kind) {
    case 'sort-papers': {
      return {
        ...state,
        ordering: {
          by: action.by as OrderBy,
          isDescending: action.isDescending,
        },
        currentPage: 0,
      };
    }
    case 'clear-sort': {
      return { ...state, ordering: null, currentPage: 0 };
    }
    case 'select-paper': {
      const alreadyExists =
        state.selectedPapers.find(
          (cid: CorpusId) => cid === action.corpusId,
        ) !== undefined;
      if (alreadyExists) {
        return state;
      } else {
        const updatedSelectPapers = state.selectedPapers.concat([
          action.corpusId,
        ]);
        // must perform numeric sort to be consistent with the server
        // state
        updatedSelectPapers.sort((a, b) => a - b);
        return { ...state, selectedPapers: updatedSelectPapers };
      }
    }
    case 'unselect-paper': {
      const updatedSelectPapers = state.selectedPapers.filter(
        (cid: CorpusId) => cid !== action.corpusId,
      );
      return { ...state, selectedPapers: updatedSelectPapers };
    }
    case 'clear-all-selected-papers': {
      return { ...state, selectedPapers: [] };
    }
    case 'update-current-page': {
      return { ...state, currentPage: action.currentPage };
    }
    case 'add-filter': {
      const alreadyExists =
        state.filters.find(
          (pf: PaperFilter) =>
            pf.value === action.value &&
            pf.categoryName === action.categoryName,
        ) !== undefined;
      if (alreadyExists) {
        return state;
      } else {
        let updatedFilters = state.filters.concat([
          {
            categoryName: action.categoryName as FacetName,
            value: action.value,
          },
        ]);
        // NOTE: make sure the sort is always consistent to make sure the state_id md5 is consistent
        updatedFilters = sortBy(updatedFilters, ['categoryName', 'value']);
        return { ...state, filters: updatedFilters };
      }
    }
    case 'remove-filter': {
      const updatedFilters = state.filters.filter(
        (pf: PaperFilter) =>
          pf.value !== action.value || pf.categoryName !== action.categoryName,
      );
      return { ...state, filters: updatedFilters };
    }
    case 'clear-all-filters': {
      return { ...state, filters: [], currentPage: 0 };
    }
  }

  return state;
}

function generateWidgetStateId(state: WidgetState): WidgetStateId {
  // make sure keys are consistently ordered for the md5 to be consistant
  const jsonData = stableStringify(state) ?? '';
  const hash = MD5(jsonData).toString(enc.Hex);
  return `ws:${hash}`;
}

export function isMatchingMessages(
  existingMessage: Message,
  incomingMessage: Message,
): boolean {
  const cleanIncomingMessage = omit(incomingMessage, ['createdTimestamp']);
  return isEqual(existingMessage, cleanIncomingMessage);
}
